import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';

declare global {
	interface Window {
		configs: { config: { Sparrow: Record<string, any> } }[];
	}
}

/**
 * @copyright © Paramount Streaming
 * @description VTG tracking service integration plugin for analytics and measurement
 *
 * **Analytics Integration:**
 * Integrates VTG's tracking service with Smart Tag's analytics system to provide
 * comprehensive video analytics and user behavior measurement across the platform.
 *
 * **Tracking Activation Logic:**
 * Uses sophisticated business logic to determine when tracking should be enabled:
 * - **Tealium Integration**: Requires utag (Tealium) presence for tag management
 * - **VTG Service Availability**: Verifies VTG tracking service and config availability
 * - **Smart Tag Control**: Respects Smart Tag's tracking disable flags and policies
 * - **Environment Adaptation**: Adjusts tracking behavior for different environments
 *
 * **Business Logic Considerations:**
 * - **Privacy Compliance**: Honors user tracking preferences and consent requirements
 * - **Performance Impact**: Optional plugin that doesn't block video playback if unavailable
 * - **Data Collection**: Enables business intelligence and user behavior analytics
 * - **Cross-Platform Tracking**: Consistent measurement across Desktop, Mobile, and Smart TV
 *
 * **VTG Integration:**
 * Uses VTG's tracking service namespace ('vtg.ts.AviaTracking') for seamless
 * integration with VTG's analytics infrastructure while maintaining Smart Tag control.
 *
 * **Business Impact:**
 * Enables data-driven decision making through comprehensive video analytics while
 * maintaining user privacy and platform performance requirements.
 */
export class TrackingServicePlugin extends AviaPlugin implements IAviaPlugin {
	// no NPM package used for this plugin
	package = undefined;
	// no namespaceKey needed since we are overriding the complete namespace
	// must set this to undefined otherwise it is inherited.
	namespaceKey = undefined;

	override get namespaceComplete(): string {
		return 'vtg.ts.AviaTracking';
	}

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TrackingServicePlugin'
			}
		);
	}

	/**
	 * @description Determines if tracking should be enabled based on environment and business logic
	 *
	 * **Tracking Activation Requirements:**
	 * Uses comprehensive checks to ensure tracking is both technically possible and
	 * business-appropriate before enabling analytics collection.
	 *
	 * **Activation Criteria:**
	 * 1. **Tealium Integration**: utag must be available for tag management system
	 * 2. **VTG Service Check**: VTG tracking service must be loaded and configured
	 * 3. **Configuration Validation**: VTG config object must be available in DOM
	 * 4. **Smart Tag Policy**: Smart Tag tracking disabled flag must not be set
	 *
	 * **Business Logic:**
	 * - **Privacy Compliance**: Respects user and content tracking preferences
	 * - **Technical Dependencies**: Ensures all required tracking infrastructure is available
	 * - **Performance Considerations**: Only enables when technically feasible
	 * - **Environment Adaptation**: Handles development vs production environments
	 *
	 * @returns {boolean} True if all tracking requirements are met and tracking should be enabled
	 */
	shouldBeUsed() {
		// unless legacy tracking is disabled and DIT DataHub plugin reference exists in DOM namespace
		const ditDataHubPluginExists = this.utils.Type.isDefined( window?.dit?.svc?.avia );
		const useLegacyTrackingOverride = this?.params?.TAG?.useLegacyTracking;

		if( ditDataHubPluginExists && !useLegacyTrackingOverride ) {
			this.logger?.log( 'Legacy tracking is disabled, DIT DataHub plugin reference exists in DOM namespace, disable legacy tracking' );
			return false;
		}

		// if utag exists in DOM namespace
		const utagExists = this.utils.Type.isDefined( window?.utag );
		// if VTG tracking service class reference exists in DOM namespace
		const vtgTrackingServiceExists = this.utils.Type.isDefined( this.loadedPlugin );
		// if VTG tracking service config exists in DOM namespace
		const vtgTrackingConfigExists = this.utils.Type.isDefined( window?.vtg?.config );
		// if TAG params doesn't indicate tracking should be disabled
		const trackingDisabled = this?.params?.TAG?.trackingDisabled || false;

		return utagExists && vtgTrackingServiceExists && vtgTrackingConfigExists && trackingDisabled === false;
	}

	getTrackingConfig = (): Record<string, any> => {
		const resource = this.tag?.params?.CONTENT?.resource;
		// Extract the multi-stream player number
		const playerNum = resource?.multiStreamPlayer;
		// Determine if multi-stream is enabled and the player number is valid
		const isMultiStream = resource?.multiStreamEnabled && this.utils.Type.isNumber( playerNum );

		if ( isMultiStream ) {
			// Try to get the config for the corresponding player index (0-based)
			const config = window?.configs?.[ playerNum - 1 ]?.config;
			if ( config ) {
				return config;
			}
		}

		// Fallback to the global VTG config if no specific config is found
		return window?.vtg?.config;
	};

	override getFactoryParams() {
		return {
			config: this.getTrackingConfig()
		};
	}
}